import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import {
  Activity, RefreshCw, Send, FileText, CheckCircle2, XCircle, Search,
  Layers, FileSpreadsheet, CalendarPlus, RefreshCcw, ShieldCheck, Clock, History
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId } from './propertySync';
import { formatDateTime } from '../../utils/dateTime';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

// Map raw einvoice_logs action -> friendly label + icon + colour
const ACTION_META = {
  lhdn_submit:          { label: 'Submitted to LHDN',    icon: Send,           color: 'text-blue-600 bg-blue-50 dark:bg-blue-500/10' },
  lhdn_check_status:    { label: 'LHDN Status Check',     icon: ShieldCheck,    color: 'text-sky-600 bg-sky-50 dark:bg-sky-500/10' },
  generate_invoice:     { label: 'Invoice Generated',     icon: FileText,       color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10' },
  manual_invoice_create:{ label: 'Manual Invoice Created',icon: FileText,       color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10' },
  booking_create:       { label: 'Booking Created',       icon: CalendarPlus,   color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-500/10' },
  consolidate:          { label: 'Consolidated B2C',      icon: Layers,         color: 'text-purple-600 bg-purple-50 dark:bg-purple-500/10' },
  excel_upload:         { label: 'Excel Upload',          icon: FileSpreadsheet,color: 'text-amber-600 bg-amber-50 dark:bg-amber-500/10' },
  beds24_sync:          { label: 'Beds24 Sync',           icon: RefreshCcw,     color: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-500/10' },
  owner_payment_create:    { label: 'Owner Payment Created',   icon: FileText,    color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10' },
  owner_payment_submit:    { label: 'Owner Payment Submitted', icon: Send,        color: 'text-blue-600 bg-blue-50 dark:bg-blue-500/10' },
  owner_payment_validated: { label: 'Owner Payment Validated', icon: ShieldCheck, color: 'text-teal-600 bg-teal-50 dark:bg-teal-500/10' },
  owner_payment_cancel:    { label: 'Owner Payment Cancelled', icon: XCircle,     color: 'text-rose-600 bg-rose-50 dark:bg-rose-500/10' },
};

const metaFor = (action) => ACTION_META[action] || { label: action || 'Activity', icon: Activity, color: 'text-slate-600 bg-slate-100 dark:bg-zinc-800' };

// Pull a human-readable detail out of the JSON payloads
function extractDetail(log) {
  const parse = (v) => { try { return typeof v === 'string' ? JSON.parse(v) : (v || {}); } catch { return {}; } };
  const req = parse(log.request_payload);
  const res = parse(log.response_payload);
  if (req.invoice_number) return `Invoice ${req.invoice_number}`;
  if (res.invoice_number) return `Invoice ${res.invoice_number}`;
  if (req.uuid || res.uuid) return `UUID ${(req.uuid || res.uuid).toString().slice(0, 18)}…`;
  if (req.count !== undefined) return `${req.count} record(s)`;
  if (req.date) return `Date ${req.date}`;
  if (res.id) return `Ref ${res.id.toString().slice(0, 8)}`;
  return '—';
}

const StatusPill = ({ status }) => {
  const ok = (status || '').toLowerCase() === 'success';
  return ok
    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400" data-testid="audit-status-success"><CheckCircle2 className="w-3.5 h-3.5" /> Success</span>
    : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 dark:text-red-400" data-testid="audit-status-error"><XCircle className="w-3.5 h-3.5" /> {status || 'Error'}</span>;
};

export default function PortalAuditTrail() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyId, setPropertyId] = useState(() => getGlobalPropertyId());

  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  const propName = useCallback((id) => {
    const p = properties.find((x) => x.id === id);
    return p ? p.property_name : (id ? `${id.slice(0, 8)}…` : '—');
  }, [properties]);

  const fetchLogs = useCallback(async () => {
    try {
      // cache-buster (_t) + no-cache headers so "Refresh" always pulls the
      // latest rows even behind a CDN / browser cache.
      const cb = `_t=${Date.now()}`;
      const url = propertyId
        ? `/api/einvoice/gateway/logs?property_id=${propertyId}&limit=200&${cb}`
        : `/api/einvoice/gateway/logs?limit=200&${cb}`;
      const { data } = await portalApi.get(url, {
        headers: { ...headers, 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      setLogs(data.logs || []);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('einvoice_token'); navigate('/einvoice/portal/auth'); }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    portalApi.get('/api/einvoice/portal/properties', { headers })
      .then((r) => setProperties(r.data.properties || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Keep in sync with the global property selector in the header
  useEffect(() => {
    const handler = (e) => setPropertyId(e.detail ? e.detail.id : null);
    window.addEventListener('property-changed', handler);
    return () => window.removeEventListener('property-changed', handler);
  }, []);

  const handleRefresh = () => { setRefreshing(true); fetchLogs(); };

  // On-demand: pull today's already-submitted owner-payment events into the trail
  const handleSyncToday = async () => {
    setSyncing(true);
    try {
      const qs = propertyId ? `?property_id=${propertyId}` : '';
      const { data } = await portalApi.post(`/api/einvoice/owner-payments/audit/sync-today${qs}`, {}, {
        headers: { ...headers, 'Cache-Control': 'no-cache' },
      });
      const n = data?.inserted || 0;
      if (n > 0) toast.success(`Added ${n} of today's owner-payment event${n > 1 ? 's' : ''} to the audit trail.`);
      else toast.info("You're up to date — no new owner-payment activity to add for today.");
      await fetchLogs();
    } catch (err) {
      toast.error('Could not sync today\'s activity. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = logs.filter((l) => {
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    if (statusFilter !== 'all' && (l.status || '').toLowerCase() !== statusFilter) return false;
    if (searchQuery) {
      const hay = `${metaFor(l.action).label} ${extractDetail(l)} ${l.request_payload || ''}`.toLowerCase();
      if (!hay.includes(searchQuery.toLowerCase())) return false;
    }
    return true;
  });

  const total = logs.length;
  const successes = logs.filter((l) => (l.status || '').toLowerCase() === 'success').length;
  const failures = total - successes;
  const lastTs = logs[0]?.created_at || logs[0]?.timestamp;

  const stats = [
    { label: 'Total Events', value: total, icon: Activity, color: 'from-blue-500 to-indigo-600' },
    { label: 'Successful', value: successes, icon: CheckCircle2, color: 'from-emerald-500 to-teal-600' },
    { label: 'Failed', value: failures, icon: XCircle, color: 'from-red-500 to-rose-600' },
    { label: 'Last Activity', value: lastTs ? formatDateTime(lastTs) : '—', icon: Clock, color: 'from-purple-500 to-pink-600', small: true },
  ];

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="portal-audit-trail">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#4169E1]" /> Audit Trail
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
              Immutable record of every e-Invoice action — submissions, validations, imports and syncs — each stamped with a UTC timestamp.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSyncToday} variant="outline" disabled={syncing} data-testid="audit-sync-today-btn"
              className="border-[#4169E1]/40 text-[#4169E1] hover:bg-[#4169E1]/5">
              <History className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : "Sync Today's Activity"}
            </Button>
            <Button onClick={handleRefresh} variant="outline" disabled={refreshing} data-testid="audit-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((s) => (
            <Card key={s.label} className="border-0 shadow-lg bg-white dark:bg-zinc-900" data-testid={`audit-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">{s.label}</p>
                  <p className={`font-bold text-slate-900 dark:text-white ${s.small ? 'text-sm' : 'text-2xl'}`}>{s.value}</p>
                </div>
                <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-lg shrink-0`}>
                  <s.icon className="w-5 h-5 text-white" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search action or detail…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="audit-search-input"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px]" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {Object.keys(ACTION_META).map((a) => (
                <SelectItem key={a} value={a}>{ACTION_META[a].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="audit-status-filter"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="border-0 shadow-lg bg-white dark:bg-zinc-900">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-log-table">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-zinc-800 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400">Timestamp (UTC)</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400">Action</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400">Detail</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400">Property</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="audit-loading">Loading audit trail…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="audit-empty">No audit events found.</td></tr>
                  ) : (
                    filtered.map((log, idx) => {
                      const meta = metaFor(log.action);
                      const Icon = meta.icon;
                      return (
                        <tr key={log.id || idx} className="border-b border-slate-50 dark:border-zinc-800/50 hover:bg-slate-50/60 dark:hover:bg-zinc-800/40" data-testid="audit-log-row">
                          <td className="px-4 py-3 text-slate-600 dark:text-gray-300 whitespace-nowrap text-xs">{formatDateTime(log.created_at || log.timestamp)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.color}`}>
                              <Icon className="w-3.5 h-3.5" /> {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-gray-300 text-xs">{extractDetail(log)}</td>
                          <td className="px-4 py-3 text-slate-500 dark:text-gray-400 text-xs whitespace-nowrap">{propName(log.property_id)}</td>
                          <td className="px-4 py-3"><StatusPill status={log.status} /></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-3">
          Showing the {logs.length} most recent events{propertyId ? ' for the selected property' : ' across all properties'}. Audit records are append-only and retained for compliance and reconciliation.
        </p>
      </div>
    </PortalLayout>
  );
}
